import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiArrowRight, FiPlus, FiTrash2, FiClock, FiHelpCircle } from 'react-icons/fi';

interface QuickPrompt {
  text: string;
  icon: React.ReactNode;
  type: 'query' | 'create' | 'delete' | 'update' | 'help';
}

interface QuickPromptsProps {
  onSelectPrompt: (prompt: string) => void;
}

export const QuickPrompts: React.FC<QuickPromptsProps> = ({ onSelectPrompt }) => {
  const { t } = useTranslation(['v0/chat', 'v0/common']);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { current } = scrollContainerRef;
      const scrollAmount = 200; // 每次滚动的像素值
      current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  
  const quickPrompts: QuickPrompt[] = [
    // 查询类提示
    { 
      text: t('v0/chat:quickPrompts.pendingTasks', '我有哪些待处理的任务？'), 
      icon: <FiHelpCircle className="text-blue-500" />,
      type: 'query'
    },
    { 
      text: t('v0/chat:quickPrompts.upcomingEvents', '接下来几天有什么活动？'), 
      icon: <FiHelpCircle className="text-blue-500" />,
      type: 'query'
    },
    { 
      text: t('v0/chat:quickPrompts.summarizeDay', '总结一下我今天的安排'), 
      icon: <FiHelpCircle className="text-blue-500" />,
      type: 'query'
    },
    
    // 创建类提示
    { 
      text: t('v0/chat:quickPrompts.createMeeting', '安排一个新的会议'), 
      icon: <FiPlus className="text-green-500" />,
      type: 'create'
    },
    { 
      text: t('v0/chat:quickPrompts.addTask', '添加一个打豆豆任务'), 
      icon: <FiPlus className="text-green-500" />,
      type: 'create'
    },
    { 
      text: t('v0/chat:quickPrompts.createReminder', '设置提醒我买蛋糕'), 
      icon: <FiPlus className="text-green-500" />,
      type: 'create'
    },
    
    // 更新类提示
    { 
      text: t('v0/chat:quickPrompts.reschedule', '重新安排下午的活动'), 
      icon: <FiClock className="text-amber-500" />,
      type: 'update'
    },
    { 
      text: t('v0/chat:quickPrompts.postponeTask', '将打豆豆任务推迟到明天'), 
      icon: <FiClock className="text-amber-500" />,
      type: 'update'
    },
    { 
      text: t('v0/chat:quickPrompts.changeTime', '修改会议时间为下午3点'), 
      icon: <FiClock className="text-amber-500" />,
      type: 'update'
    },
    
    // 删除类提示
    { 
      text: t('v0/chat:quickPrompts.deleteTask', '删除打豆豆任务'), 
      icon: <FiTrash2 className="text-red-500" />,
      type: 'delete'
    },
    { 
      text: t('v0/chat:quickPrompts.cancelMeeting', '取消今天的会议'), 
      icon: <FiTrash2 className="text-red-500" />,
      type: 'delete'
    }
  ];

  return (
    <div className="relative w-full bg-surface-light dark:bg-surface-dark">
      {/* 左右滚动按钮 */}
      <button 
        onClick={() => scroll('left')} 
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-full p-1 shadow-md border border-gray-200 dark:border-gray-700 opacity-80 hover:opacity-100"
        aria-label={t('v0/common:actions.scrollLeft', '向左滚动')}
      >
        <FiArrowLeft className="text-gray-600 dark:text-gray-300" />
      </button>
      
      <button 
        onClick={() => scroll('right')} 
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-full p-1 shadow-md border border-gray-200 dark:border-gray-700 opacity-80 hover:opacity-100"
        aria-label={t('v0/common:actions.scrollRight', '向右滚动')}
      >
        <FiArrowRight className="text-gray-600 dark:text-gray-300" />
      </button>
      
      {/* 快速提示滚动容器 */}
      <div 
        ref={scrollContainerRef} 
        className="flex overflow-x-auto py-2 px-8 gap-2 hide-scrollbar"
      >
        {quickPrompts.map((prompt, index) => (
          <button
            key={index}
            onClick={() => onSelectPrompt(prompt.text)}
            className={`
              flex items-center gap-2 whitespace-nowrap px-3 py-1.5 rounded-full min-w-max
              border text-sm font-medium transition-colors duration-150
              ${prompt.type === 'query' ? 'border-blue-200 bg-blue-50 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/30 dark:hover:bg-blue-800/50' : ''}
              ${prompt.type === 'create' ? 'border-green-200 bg-green-50 hover:bg-green-100 dark:border-green-800 dark:bg-green-900/30 dark:hover:bg-green-800/50' : ''}
              ${prompt.type === 'update' ? 'border-amber-200 bg-amber-50 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/30 dark:hover:bg-amber-800/50' : ''}
              ${prompt.type === 'delete' ? 'border-red-200 bg-red-50 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/30 dark:hover:bg-red-800/50' : ''}
              ${prompt.type === 'help' ? 'border-purple-200 bg-purple-50 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-900/30 dark:hover:bg-purple-800/50' : ''}
              text-gray-700 dark:text-gray-200
            `}
          >
            {prompt.icon}
            <span>{prompt.text}</span>
          </button>
        ))}
      </div>

      {/* 隐藏滚动条样式 */}
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;     /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;  /* Chrome, Safari, Opera */
          }
        `}
      </style>
    </div>
  );
};

// 当还没有消息时显示的快速提示组件
export const EmptyStateQuickPrompts: React.FC<QuickPromptsProps> = ({ onSelectPrompt }) => {
  const { t } = useTranslation(['v0/chat', 'v0/common']);
  
  const quickPrompts = [
    { 
      text: t('v0/chat:quickPrompts.pendingTasks', '我有哪些待处理的任务？'), 
      icon: <FiHelpCircle className="text-blue-500" /> 
    },
    { 
      text: t('v0/chat:quickPrompts.addTask', '添加一个打豆豆任务'), 
      icon: <FiPlus className="text-green-500" /> 
    },
    { 
      text: t('v0/chat:quickPrompts.summarizeDay', '总结一下我今天的安排'), 
      icon: <FiHelpCircle className="text-blue-500" /> 
    }
  ];

  return (
    <div className="mt-6 space-y-2">
      <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-3">
        {t('v0/chat:quickPrompts.tryThese', '可以尝试以下提示:')}
      </p>
      {quickPrompts.map((prompt, index) => (
        <button
          key={index}
          onClick={() => onSelectPrompt(prompt.text)}
          className="flex items-center justify-center gap-2 w-full max-w-xs mx-auto py-2 px-4 rounded-lg 
                    bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600
                    hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-150"
        >
          {prompt.icon}
          <span className="text-sm text-gray-700 dark:text-gray-200">{prompt.text}</span>
        </button>
      ))}
    </div>
  );
}; 